import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  buildingCodesTooltip,
  housekeepingTooltip,
} from "../../../constants/TooltipInformation";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const HouseKeepingSections = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [houseKeepingSectionCode, setHouseKeepingSectionCode] = useState<
    string | null
  >(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [houseKeepingSectionName, setHouseKeepingSectionName] = useState<
    string | null
  >(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [buildingList, setBuildingList] = useState<string[]>([]);
  const [buildingName, setBuildingName] = useState<string | null>(null);
  const [buildingError, setBuildingError] = useState<boolean>(false);
  const [houseKeepingInformation, setHouseKeepingInformation] =
    useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const houseKeepingPayload = {
    code: houseKeepingSectionCode,
    name: houseKeepingSectionName,
    buildingCode: buildingName,
    isActive: active ?? true,
  };

  const addHouseKeeping = () => {
    if (!validateInput()) return;
    if (
      !houseKeepingSectionCode ||
      houseKeepingSectionCode === null ||
      houseKeepingSectionCode === ""
    ) {
      setCodeError(true);
      return;
    }
    if (
      !houseKeepingSectionName ||
      houseKeepingSectionName === null ||
      houseKeepingSectionName === ""
    ) {
      setNameError(true);
      return;
    }
    if (!buildingName || buildingName === null || buildingName === "") {
      setBuildingError(true);
      return;
    }
    if (
      checkDuplicate(
        Array.isArray(houseKeepingInformation) ? houseKeepingInformation : [],
        DNDFieldsEnum.HOUSEKEEPINGCODE,
        houseKeepingSectionCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setHouseKeepingInformation((prev: any) => {
      return updateData(
        Array.isArray(prev)
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...houseKeepingPayload,
              },
            ]
          : [
              {
                id: 1,
                ...houseKeepingPayload,
              },
            ],
      );
    });
    setLastUpdatedRecord(houseKeepingPayload);
    editHouseKeeping(null);
    setEditClicked(false);
  };

  const editHouseKeeping = (list: any) => {
    setBuildingError(false);
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setHouseKeepingSectionCode(list?.code);
    setHouseKeepingSectionName(list?.name);
    setBuildingName(list?.buildingCode);
    setActive(list ? list.isActive : true);
  };

  const updateHouseKeepingSection = () => {
    if (!validateInput()) return;
    if (
      !houseKeepingSectionCode ||
      houseKeepingSectionCode === null ||
      houseKeepingSectionCode === ""
    ) {
      setCodeError(true);
      return;
    }
    if (
      !houseKeepingSectionName ||
      houseKeepingSectionName === null ||
      houseKeepingSectionName === ""
    ) {
      setNameError(true);
      return;
    }
    if (!buildingName || buildingName === null || buildingName === "") {
      setBuildingError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(houseKeepingInformation)
          ? houseKeepingInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.HOUSEKEEPINGCODE,
        houseKeepingSectionCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setHouseKeepingInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...houseKeepingPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editHouseKeeping(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RoomPropertyEnum.HousekeepingSections} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteHouseKeeping = (id: number) => {
    setHouseKeepingInformation(deleteItem(houseKeepingInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    setBuildingError(false);
    let isValid = true;
    if (
      houseKeepingSectionCode &&
      !validateString(
        houseKeepingSectionCode,
        validationLength.houseKeepingCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }
    if (
      houseKeepingSectionName &&
      !validateString(
        houseKeepingSectionName,
        validationLength.houseKeepingName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      isValid = false;
    }

    return isValid;
  };

  useEffect(() => {
    data(houseKeepingInformation);
    editClicked &&
      setSelectedId(
        checkData(houseKeepingInformation, selectedId) ? selectedId : null,
      );
  }, [houseKeepingInformation]);

  useEffect(() => {
    if (fieldValues) {
      setHouseKeepingInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.HousekeepingSections])
          ? fieldValues[RoomPropertyEnum.HousekeepingSections]
          : fieldValues[RoomPropertyEnum.HousekeepingSections]
            ? [fieldValues[RoomPropertyEnum.HousekeepingSections]]
            : null,
      );
      setBuildingList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.buildingCodes],
          DNDFieldsEnum.BUILDINGCODE,
          DNDFieldsEnum.BUILDINGNAME,
        ),
      );
    }
  }, []);

  useEffect(() => {
    validateInput();
  }, [houseKeepingSectionCode, houseKeepingSectionName, buildingName]);

  const houseKeepingSectionCodeHelperText = codeError
    ? !(houseKeepingSectionCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.houseKeepingSections.houseKeepingSectionCode
    : versaFieldHelperText.houseKeepingSections.houseKeepingSectionCode;

  const houseKeepingSectionNameHelperText = nameError
    ? !(houseKeepingSectionName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.houseKeepingSections.houseKeepingSectionName
    : versaFieldHelperText.houseKeepingSections.houseKeepingSectionName;

  const buildingCodeHelperText = buildingError
    ? versaFieldErrors.required
    : versaFieldHelperText.houseKeepingSections.buildingCode;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Housekeeping Section
              <Tooltip title={tooltipComponent(housekeepingTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editHouseKeeping(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editHouseKeeping(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Housekeeping Section Code
                <Tooltip
                  title={tooltipComponent(housekeepingTooltip.sectionCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="housekeepingSectionCode"
                variant="outlined"
                placeholder="Housekeeping Section Code"
                value={houseKeepingSectionCode ?? ""}
                error={codeError}
                sx={{ width: "100%" }}
                helperText={houseKeepingSectionCodeHelperText}
                onChange={(e: any) => {
                  setHouseKeepingSectionCode(e.target.value.toUpperCase());
                  setCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Housekeeping Section Name
                <Tooltip
                  title={tooltipComponent(housekeepingTooltip.sectionName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="housekeepingSectionName"
                variant="outlined"
                placeholder="Housekeeping Section Name"
                value={houseKeepingSectionName ?? ""}
                error={nameError}
                sx={{ width: "100%" }}
                helperText={houseKeepingSectionNameHelperText}
                onChange={(e: any) => {
                  setHouseKeepingSectionName(e.target.value);
                  setNameError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Building Name
                <Tooltip
                  title={tooltipComponent(housekeepingTooltip.buildingName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                disablePortal
                options={removeSampleTestArray(buildingList)}
                sx={{ width: "100%" }}
                value={buildingName ? buildingName : ""}
                disabled={!buildingList}
                onChange={(_, newValue) => {
                  setBuildingName(newValue);
                  setBuildingError(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Building Name"
                    id="buildingName"
                    error={buildingError}
                    helperText={buildingCodeHelperText}
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            {/* <Tooltip title={tooltipComponent("")}>
              <InfoIcon fontSize="small" />
            </Tooltip> */}
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateHouseKeepingSection : addHouseKeeping}
              id="addNewHousekeepingSection"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Housekeeping
              Section
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Housekeeping Section Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {houseKeepingInformation ? (
                <DraggableItemList
                  itemData={houseKeepingInformation}
                  onOrderUpdate={(order) => setHouseKeepingInformation(order)}
                  onEdit={(info) => {
                    editHouseKeeping(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteHouseKeeping(id)}
                  code={DNDFieldsEnum.HOUSEKEEPINGCODE}
                  name={DNDFieldsEnum.HOUSEKEEPINGNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Housekeeping Section Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default HouseKeepingSections;
